import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry';

const RubiksCube = ({ width = 300, height = 300 }) => {
    const mountRef = useRef(null);
    const sceneRef = useRef(null);
    const rendererRef = useRef(null);
    const cameraRef = useRef(null);
    const cubeGroupRef = useRef(null);
    const lightsRef = useRef([]);
    const piecesRef = useRef([]);
    const animationFrameRef = useRef(null);
    const lastTimeRef = useRef(0);
    const currentRotationRef = useRef(null);

    useEffect(() => {
        // Cleanup any existing canvas before creating new one
        if (mountRef.current) {
            while (mountRef.current.firstChild) {
                mountRef.current.removeChild(mountRef.current.firstChild);
            }
        }

        // Scene setup
        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000);
        const renderer = new THREE.WebGLRenderer({ 
            antialias: true,
            alpha: true // Enable transparency
        });
        
        renderer.setSize(width, height);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        renderer.setClearColor(0x000000, 0); // Make background transparent
        
        mountRef.current.appendChild(renderer.domElement);

        // Store refs
        sceneRef.current = scene;
        rendererRef.current = renderer;
        cameraRef.current = camera;

        // Create environment map
        const cubeTextureLoader = new THREE.CubeTextureLoader();
        const environmentMap = cubeTextureLoader.load([
            'https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/textures/cube/SwedishRoyalCastle/px.jpg',
            'https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/textures/cube/SwedishRoyalCastle/nx.jpg',
            'https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/textures/cube/SwedishRoyalCastle/py.jpg',
            'https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/textures/cube/SwedishRoyalCastle/ny.jpg',
            'https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/textures/cube/SwedishRoyalCastle/pz.jpg',
            'https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/textures/cube/SwedishRoyalCastle/nz.jpg'
        ]);
        scene.environment = environmentMap;

        // Constants - made smaller
        const cubeSize = 0.6;
        const gap = 0.03;
        const roundedRadius = 0.06;

        // Materials
        const blackMaterial = new THREE.MeshPhysicalMaterial({
            color: 0x000000,
            metalness: 0.9,
            roughness: 0.1,
            envMapIntensity: 1.0,
            clearcoat: 1.0,
            clearcoatRoughness: 0,
            side: THREE.DoubleSide,
            reflectivity: 0.8
        });

        const orangeEdges = new THREE.LineBasicMaterial({
            color: 0xFF6B00,
            linewidth: 1
        });

        // Create cube group
        const cubeGroup = new THREE.Group();
        cubeGroupRef.current = cubeGroup;

        // Function to create a single cube piece with orange edges
        const createCubePiece = (x, y, z) => {
            const group = new THREE.Group();
            
            // Main cube
            const geometry = new RoundedBoxGeometry(cubeSize, cubeSize, cubeSize, 4, roundedRadius);
            const cube = new THREE.Mesh(geometry, blackMaterial.clone());
            cube.castShadow = true;
            cube.receiveShadow = true;
            
            // Add orange edges
            const edges = new THREE.EdgesGeometry(geometry);
            const line = new THREE.LineSegments(edges, orangeEdges);
            
            group.add(cube);
            group.add(line);
            
            group.position.x = (x - 1) * (cubeSize + gap);
            group.position.y = (y - 1) * (cubeSize + gap);
            group.position.z = (z - 1) * (cubeSize + gap);
            
            group.userData.position = {x, y, z};
            return group;
        };

        // Create all pieces
        const pieces = [];
        for (let x = 0; x < 3; x++) {
            for (let y = 0; y < 3; y++) {
                for (let z = 0; z < 3; z++) {
                    const piece = createCubePiece(x, y, z);
                    pieces.push(piece);
                    cubeGroup.add(piece);
                }
            }
        }
        piecesRef.current = pieces;

        // Add cube group to scene
        scene.add(cubeGroup);

        // Setup lighting
        const lightPositions = [
            { pos: [5, 5, 5], intensity: 0.4 },
            { pos: [-5, 5, 5], intensity: 0.4 },
            { pos: [5, -5, 5], intensity: 0.4 },
            { pos: [5, 5, -5], intensity: 0.4 },
            { pos: [-5, -5, 5], intensity: 0.4 },
            { pos: [-5, 5, -5], intensity: 0.4 },
            { pos: [5, -5, -5], intensity: 0.4 },
            { pos: [-5, -5, -5], intensity: 0.4 }
        ];

        const lights = lightPositions.map(({ pos, intensity }) => {
            const light = new THREE.SpotLight(0xFF6B00, intensity * 0.8);
            light.position.set(...pos);
            light.castShadow = true;
            light.shadow.bias = -0.0001;
            light.shadow.mapSize.width = 1024;
            light.shadow.mapSize.height = 1024;
            light.target = cubeGroup;
            light.angle = Math.PI / 4;
            light.penumbra = 0.5;
            light.decay = 1;
            light.distance = 20;
            scene.add(light);
            return light;
        });
        lightsRef.current = lights;

        // Add hemisphere light for better ambient illumination
        const hemisphereLight = new THREE.HemisphereLight(0xFF6B00, 0x000000, 0.4);
        scene.add(hemisphereLight);

        // Add ambient light for base illumination
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.2);
        scene.add(ambientLight);

        // Make scene background transparent
        scene.background = null;

        // Camera position - moved closer
        camera.position.set(3, 3, 3);
        camera.lookAt(0, 0, 0);

        // Controls
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controls.enableZoom = false; // Disable zooming
        controls.minPolarAngle = Math.PI / 4; // Limit vertical rotation
        controls.maxPolarAngle = Math.PI * 3/4;
        
        // Animation functions
        const updatePiecePositions = (axis, layerIndex, angle) => {
            const rotationMatrix = new THREE.Matrix4();
            const rotationQuaternion = new THREE.Quaternion();
            const pivotPoint = new THREE.Vector3(0, 0, 0);
            const rotationAxis = new THREE.Vector3(
                axis === 'x' ? 1 : 0,
                axis === 'y' ? 1 : 0,
                axis === 'z' ? 1 : 0
            );

            const layerPieces = pieces.filter(piece => {
                const pos = piece.userData.position;
                switch(axis) {
                    case 'x': return Math.round(pos.x) === layerIndex;
                    case 'y': return Math.round(pos.y) === layerIndex;
                    case 'z': return Math.round(pos.z) === layerIndex;
                    default: return false;
                }
            });

            rotationMatrix.makeRotationAxis(rotationAxis, angle);
            
            layerPieces.forEach(piece => {
                const relativePosition = piece.position.clone().sub(pivotPoint);
                relativePosition.applyMatrix4(rotationMatrix);
                piece.position.copy(relativePosition.add(pivotPoint));
                
                piece.quaternion.premultiply(rotationQuaternion.setFromRotationMatrix(rotationMatrix));
                
                const pos = piece.userData.position;
                const oldPos = { ...pos };
                
                if (axis === 'x') {
                    pos.y = Math.round(2 - oldPos.z);
                    pos.z = Math.round(oldPos.y);
                } else if (axis === 'y') {
                    pos.x = Math.round(oldPos.z);
                    pos.z = Math.round(2 - oldPos.x);
                } else {
                    pos.x = Math.round(2 - oldPos.y);
                    pos.y = Math.round(oldPos.x);
                }
            });
        };

        const resetPiecePositions = () => {
            pieces.forEach(piece => {
                const pos = piece.userData.position;
                piece.position.set(
                    (pos.x - 1) * (cubeSize + gap),
                    (pos.y - 1) * (cubeSize + gap),
                    (pos.z - 1) * (cubeSize + gap)
                );
            });
        };

        const rotateFace = () => {
            if (currentRotationRef.current) return;
            
            const axis = ['x', 'y', 'z'][Math.floor(Math.random() * 3)];
            const layerIndex = Math.floor(Math.random() * 3);
            const direction = Math.random() > 0.5 ? 1 : -1;
            
            currentRotationRef.current = {
                axis,
                layerIndex,
                direction,
                totalAngle: 0
            };
        };

        // Animation loop
        const animate = (time) => {
            const deltaTime = (time - lastTimeRef.current) / 2000;
            lastTimeRef.current = time;
            
            const time2 = time * 0.0005;
            const xAngle = Math.sin(time2) * Math.PI;
            const yAngle = Math.cos(time2 * 0.7) * Math.PI;
            const zAngle = Math.sin(time2 * 0.5) * Math.PI * 0.5;
            
            cubeGroup.rotation.x = xAngle;
            cubeGroup.rotation.y = yAngle;
            cubeGroup.rotation.z = zAngle;
            
            if (currentRotationRef.current) {
                const rotation = currentRotationRef.current;
                const angleThisFrame = Math.PI * deltaTime * rotation.direction;
                rotation.totalAngle += Math.abs(angleThisFrame);
                
                if (rotation.totalAngle >= Math.PI / 2) {
                    const remainingAngle = (Math.PI / 2 - (rotation.totalAngle - Math.abs(angleThisFrame))) 
                                        * Math.sign(angleThisFrame);
                    updatePiecePositions(rotation.axis, rotation.layerIndex, remainingAngle);
                    resetPiecePositions();
                    currentRotationRef.current = null;
                    setTimeout(rotateFace, 1000);
                } else {
                    updatePiecePositions(rotation.axis, rotation.layerIndex, angleThisFrame);
                }
            }
            
            controls.update();
            renderer.render(scene, camera);
            animationFrameRef.current = requestAnimationFrame(animate);
        };

        // Start animation
        setTimeout(rotateFace, 1000);
        animate(0);

        // Handle container resize instead of window
        const handleResize = () => {
            if (mountRef.current) {
                const newWidth = mountRef.current.clientWidth;
                const newHeight = mountRef.current.clientHeight;
                
                camera.aspect = newWidth / newHeight;
                camera.updateProjectionMatrix();
                renderer.setSize(newWidth, newHeight);
            }
        };

        // Cleanup
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
            if (mountRef.current && renderer.domElement) {
                mountRef.current.removeChild(renderer.domElement);
            }
            // Dispose of geometries and materials
            pieces.forEach(piece => {
                piece.children.forEach(child => {
                    if (child.geometry) child.geometry.dispose();
                    if (child.material) child.material.dispose();
                });
            });
            renderer.dispose();
        };
    }, [width, height]);

    return (
        <div 
            ref={mountRef} 
            style={{ 
                width: width,
                height: height,
                margin: 'auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative' 
            }} 
        />
    );
};

export default RubiksCube;
